/**
 * The plan catalog, shared by the redesigned Plan and Screens steps.
 *
 * Both steps need the same three things off the backend `planFeatures` catalog — price for
 * a currency/interval, the feature bullet list, and where a plan sits in the tier order —
 * so this pulls that logic into one place instead of each step reimplementing it.
 *
 * `no-cache` is REQUIRED here, not a copied habit. `PlanFeatures` objects normalize by `_id`,
 * and the wizard's own single-plan `PLAN_FEATURES_QUERY` (network-only, for the selected
 * alias) writes the same entities — with `features` coming back null for the plans it does
 * not target. Those nulls land on top of the catalog's real feature lists, so reading this
 * query through the cache shows features for whichever plan happened to be written last and
 * empty lists for the rest. Verified: Standard kept 44 features while proPlus/engage held
 * `features: null`. Production's PlanComparisonTable forces no-cache for the same reason.
 */
import { useMemo } from 'react';
import { useQuery } from '@apollo/client/react';
import { ALL_PLAN_FEATURES_QUERY } from '@scio3/graphql-client';
import { BILLING_INTERVAL } from '@scio3/shared-types';
import type { BillingInterval } from '@scio3/shared-types';

export interface PlanFeatureItem {
  name?: string;
  description?: string;
  enable?: boolean;
  checked?: boolean;
  isHide?: boolean;
}

interface PlanPriceEntry {
  price?: number;
  name?: string;
  discount?: number;
  discount_text?: string;
  stripe_plan_id?: string;
}

export interface CatalogPlan {
  _id: string;
  alias?: string;
  name?: string;
  order?: number;
  plans?: Record<string, Record<string, PlanPriceEntry>>;
  features?: PlanFeatureItem[];
  isActive?: boolean;
  learnMoreURL?: string;
}

interface AllPlanFeaturesResponse {
  planFeatures: { page: { edges: Array<{ node: CatalogPlan }> } };
}

/** Per-screen price for a plan at a currency + interval. Yearly entries are annual totals. */
export function getPlanPriceEntry(
  plan: CatalogPlan | undefined,
  currency: string,
  interval: BillingInterval,
): PlanPriceEntry | undefined {
  const byCurrency = plan?.plans?.[currency];
  if (!byCurrency) return undefined;
  // Exact key first (matches how useSubscriptionWizard reads prices), then fall back to
  // monthly so a plan with no yearly entry still shows a number rather than 0.
  return byCurrency[interval] ?? byCurrency[BILLING_INTERVAL.MONTHLY];
}

/** Monthly-equivalent per-screen price, so Monthly and Yearly are comparable side by side. */
export function getMonthlyEquivalent(
  plan: CatalogPlan | undefined,
  currency: string,
  interval: BillingInterval,
): number {
  const raw = getPlanPriceEntry(plan, currency, interval)?.price ?? 0;
  const isYearly =
    interval === BILLING_INTERVAL.YEARLY || interval === BILLING_INTERVAL.YEARLY_FREEDEVICE;
  return isYearly ? raw / 12 : raw;
}

/** Visible feature bullets — same rule as production: not hidden, has display text. */
export function getPlanFeatures(plan: CatalogPlan | undefined): PlanFeatureItem[] {
  return (plan?.features ?? []).filter((f) => !f.isHide && f.description?.trim());
}

/**
 * Plan aliases do NOT agree on case across the system: the catalog says `proPlus`, while
 * `company.payment.planFeature` (what the account is actually on) says `proplus`. A direct
 * `===` therefore fails to recognise a Pro Plus account as being on Pro Plus — which is why
 * production's cards show "Select" instead of "Current Plan" for those users. Every alias
 * comparison in the experiment goes through this.
 */
export function isSameAlias(a: string | undefined | null, b: string | undefined | null): boolean {
  if (!a || !b) return false;
  return a.toLowerCase() === b.toLowerCase();
}

export function usePlanCatalog(minPlanRequired?: string) {
  const { data, loading } = useQuery<AllPlanFeaturesResponse>(ALL_PLAN_FEATURES_QUERY, {
    fetchPolicy: 'no-cache',
  });

  return useMemo(() => {
    const all = (data?.planFeatures?.page?.edges ?? []).map((e) => e.node);
    const freePlan = all.find((n) => n.alias === 'free') ?? null;
    const minOrder = minPlanRequired
      ? (all.find((n) => isSameAlias(n.alias, minPlanRequired))?.order ?? 0)
      : 0;
    // Retired tiers carry isActive=false and must never render as a selectable card, even
    // for an account currently on one (same rule as production PlanComparisonTable).
    const plans = all
      .filter((n) => n.isActive !== false && n.alias !== 'free' && (n.order ?? 0) >= minOrder)
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

    const orderOf = (alias: string | undefined | null) =>
      all.find((n) => isSameAlias(n.alias, alias))?.order ?? 0;

    return {
      plans,
      freePlan,
      orderOf,
      byAlias: (a?: string) => all.find((n) => isSameAlias(n.alias, a)),
      loading,
    };
  }, [data, minPlanRequired, loading]);
}
